/**
 * Tema claro ou escuro.
 *
 * Vive fora da sessão de propósito: a preferência vale **antes do login**, e é
 * do navegador, não do usuário. Quem abre o painel numa sala clara não deveria
 * levar um flash escuro na cara para só então poder escolher.
 */
import { create } from "zustand";

export type Theme = "light" | "dark";

/** Compartilhada com o script do `index.html` — mudar aqui exige mudar lá. */
export const THEME_STORAGE_KEY = "mt.theme";

export function readStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
}

/** Sem preferência gravada, segue o sistema — é o padrão menos surpreendente. */
export function getSystemTheme(): Theme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  // O `index.html` já aplicou a classe antes do React montar; aqui só se lê o
  // mesmo valor, para os dois não discordarem.
  theme: readStoredTheme() ?? getSystemTheme(),

  setTheme: (theme) => {
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* armazenamento bloqueado: a escolha vale nesta aba e não sobrevive ao F5 */
    }
    set({ theme });
  },

  toggle: () => get().setTheme(get().theme === "dark" ? "light" : "dark"),
}));
